import React, { useRef, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { toast } from 'sonner';
import api from '@/lib/api';
import { ScanSearch, Loader2, CheckCircle2, XCircle, FileScan, BookmarkCheck } from 'lucide-react';

const LINE_BADGE = {
    exact: { label: 'Reconnu', cls: 'bg-emerald-100 text-emerald-700' },
    fuzzy: { label: 'Approximatif', cls: 'bg-amber-100 text-amber-700' },
    new: { label: 'Non reconnu', cls: 'bg-red-100 text-red-700' },
};

const OkBadge = ({ ok, okLabel, koLabel, testId }) => (
    <Badge className={ok ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'} data-testid={testId}>
        {ok ? <CheckCircle2 className="w-3 h-3 mr-1" /> : <XCircle className="w-3 h-3 mr-1" />}
        {ok ? okLabel : koLabel}
    </Badge>
);

export default function InvoiceAuditDialog({ open, onClose }) {
    const fileRef = useRef(null);
    const [file, setFile] = useState(null);
    const [job, setJob] = useState(null); // {progress, percent}
    const [result, setResult] = useState(null);
    const [openInv, setOpenInv] = useState({});
    const [confirming, setConfirming] = useState({});

    const confirmAlias = async (i, j, line) => {
        const key = `${i}-${j}`;
        setConfirming(c => ({ ...c, [key]: true }));
        try {
            await api.post('/invoice-audit/confirm-alias', {
                supplier_product_id: line.matched_supplier_product_id,
                ref: line.ref || '',
                description: line.description || '',
            });
            setResult(r => {
                const invoices = [...r.invoices];
                const lines = [...invoices[i].lines];
                lines[j] = { ...lines[j], status: 'exact', match_source: 'alias confirmé' };
                const stats = { ...invoices[i].stats, exact: invoices[i].stats.exact + 1, fuzzy: invoices[i].stats.fuzzy - 1 };
                invoices[i] = { ...invoices[i], lines, stats };
                return { ...r, invoices, totals: { ...r.totals, exact: r.totals.exact + 1, fuzzy: r.totals.fuzzy - 1 } };
            });
            toast.success('Alias mémorisé — cette ligne sera reconnue exactement sur les prochaines factures');
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Erreur lors de la confirmation');
        } finally {
            setConfirming(c => ({ ...c, [key]: false }));
        }
    };

    const reset = () => { setFile(null); setJob(null); setResult(null); setOpenInv({}); };

    const analyze = async () => {
        if (!file) return;
        setResult(null);
        setJob({ progress: 'Envoi du PDF…', percent: 1 });
        try {
            const fd = new FormData();
            fd.append('pdf', file);
            const res = await api.post('/invoice-audit/analyze', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
            const jobId = res.data.job_id;
            const poll = async () => {
                try {
                    const st = (await api.get(`/invoice-audit/status/${jobId}`)).data;
                    if (st.status === 'done') { setJob(null); setResult(st.result); return; }
                    if (st.status === 'error') { setJob(null); toast.error(st.error || 'Erreur d\'analyse'); return; }
                    setJob({ progress: st.progress, percent: st.percent });
                    setTimeout(poll, 2000);
                } catch { setTimeout(poll, 3000); }
            };
            setTimeout(poll, 2000);
        } catch (e) {
            setJob(null);
            toast.error(e.response?.data?.detail || 'Erreur lors de l\'envoi');
        }
    };

    const pct = (a, b) => (b ? Math.round((a / b) * 100) : 0);

    return (
        <Dialog open={open} onOpenChange={(v) => { if (!v) { reset(); onClose(); } }}>
            <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto" data-testid="invoice-audit-dialog">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2 text-base">
                        <ScanSearch className="w-5 h-5 text-orange-500" /> Contrôle de reconnaissance IA
                    </DialogTitle>
                </DialogHeader>
                <p className="text-xs text-slate-500 -mt-2">
                    Analyse en lecture seule : aucune réception n'est créée. Chargez un PDF (même multi-fournisseurs)
                    pour vérifier la reconnaissance des fournisseurs, des sites et des produits.
                </p>
                {!result && (
                    <div className="space-y-3">
                        <input ref={fileRef} type="file" accept="application/pdf" className="hidden"
                            onChange={(e) => setFile(e.target.files?.[0] || null)} data-testid="audit-file-input" />
                        <Button variant="outline" onClick={() => fileRef.current?.click()} className="w-full h-16 border-dashed" data-testid="audit-pick-file-btn">
                            <FileScan className="w-5 h-5 mr-2 text-slate-400" />
                            {file ? file.name : 'Choisir un PDF de factures…'}
                        </Button>
                        {job ? (
                            <div className="space-y-1.5">
                                <Progress value={job.percent} />
                                <p className="text-xs text-slate-500 flex items-center gap-1.5"><Loader2 className="w-3 h-3 animate-spin" /> {job.progress}</p>
                            </div>
                        ) : (
                            <Button onClick={analyze} disabled={!file} className="btn-primary w-full" data-testid="audit-analyze-btn">
                                <ScanSearch className="w-4 h-4 mr-2" /> Analyser
                            </Button>
                        )}
                    </div>
                )}
                {result && (
                    <div className="space-y-3">
                        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2" data-testid="audit-summary">
                            {[
                                { label: 'Factures', val: result.invoices.length },
                                { label: 'Fournisseurs OK', val: `${result.totals.suppliers_ok}/${result.invoices.length}` },
                                { label: 'Sites OK', val: `${result.totals.sites_ok}/${result.invoices.length}` },
                                { label: 'Produits reconnus', val: `${pct(result.totals.exact + result.totals.fuzzy, result.totals.lines)}%` },
                            ].map((c, i) => (
                                <div key={i} className="border rounded-lg px-3 py-2 bg-white">
                                    <div className="text-[10px] text-slate-400 uppercase">{c.label}</div>
                                    <div className="text-lg font-bold">{c.val}</div>
                                </div>
                            ))}
                        </div>
                        {result.invoices.map((inv, i) => {
                            const reco = pct(inv.stats.exact + inv.stats.fuzzy, inv.lines.length || 1);
                            return (
                                <div key={i} className="border rounded-lg bg-white" data-testid={`audit-invoice-${i}`}>
                                    <button type="button" className="w-full text-left px-3 py-2 space-y-1.5"
                                        onClick={() => setOpenInv(o => ({ ...o, [i]: !o[i] }))} data-testid={`audit-invoice-toggle-${i}`}>
                                        <div className="flex flex-wrap items-center gap-2">
                                            <span className="text-sm font-bold">{inv.invoice_number || 'Sans n°'}</span>
                                            <span className="text-xs text-slate-400">{inv.invoice_date}</span>
                                            <span className={`text-xs font-bold ml-auto ${reco >= 90 ? 'text-emerald-600' : reco >= 60 ? 'text-amber-600' : 'text-red-600'}`}>{reco}% reconnu</span>
                                        </div>
                                        <div className="flex flex-wrap gap-1.5">
                                            <OkBadge ok={!!inv.supplier} okLabel={inv.supplier?.name} koLabel={`Fournisseur ? « ${inv.supplier_name_raw || '—'} »`} testId={`audit-supplier-${i}`} />
                                            <OkBadge ok={!!inv.site} okLabel={inv.site?.name} koLabel={`Site ? « ${inv.client_name_raw || '—'} »`} testId={`audit-site-${i}`} />
                                            <OkBadge ok={inv.has_calibration} okLabel="Calibré" koLabel="Non calibré" testId={`audit-calib-${i}`} />
                                            <Badge className="bg-slate-100 text-slate-600">{inv.stats.exact} exact · {inv.stats.fuzzy} approx · {inv.stats.new} inconnus</Badge>
                                        </div>
                                    </button>
                                    {openInv[i] && (
                                        <div className="border-t overflow-x-auto">
                                            <table className="w-full text-xs">
                                                <thead className="bg-slate-50 text-slate-500">
                                                    <tr>
                                                        <th className="px-2 py-1.5 text-left">Réf</th>
                                                        <th className="px-2 py-1.5 text-left">Désignation facture</th>
                                                        <th className="px-2 py-1.5 text-left">Produit associé</th>
                                                        <th className="px-2 py-1.5 text-center">Statut</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {inv.lines.map((l, j) => {
                                                        const b = LINE_BADGE[l.status] || LINE_BADGE.new;
                                                        return (
                                                            <tr key={j} className="border-t">
                                                                <td className="px-2 py-1 text-slate-400">{l.ref}</td>
                                                                <td className="px-2 py-1">{l.description}</td>
                                                                <td className="px-2 py-1 text-slate-500">{l.matched_product_name || '—'}</td>
                                                                <td className="px-2 py-1 text-center">
                                                                    <div className="flex items-center justify-center gap-1.5">
                                                                        <Badge className={b.cls}>{b.label}</Badge>
                                                                        {l.status === 'fuzzy' && l.matched_supplier_product_id && (
                                                                            <Button size="sm" variant="outline" className="h-6 px-2 text-[10px] border-emerald-300 text-emerald-700 hover:bg-emerald-50"
                                                                                disabled={confirming[`${i}-${j}`]}
                                                                                onClick={() => confirmAlias(i, j, l)}
                                                                                data-testid={`audit-confirm-alias-${i}-${j}`}>
                                                                                {confirming[`${i}-${j}`] ? <Loader2 className="w-3 h-3 animate-spin" /> : <><BookmarkCheck className="w-3 h-3 mr-1" /> Confirmer</>}
                                                                            </Button>
                                                                        )}
                                                                    </div>
                                                                </td>
                                                            </tr>
                                                        );
                                                    })}
                                                </tbody>
                                            </table>
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                        <Button variant="outline" onClick={reset} className="w-full" data-testid="audit-restart-btn">Analyser un autre PDF</Button>
                    </div>
                )}
            </DialogContent>
        </Dialog>
    );
}
